import { type BaseItem } from './common.js';

enum _Permission {
  VoiceAssistantConfigurationAdmin = 'VOICE_ASSISTANT_CONFIGURATION_ADMIN',
  Broadcast = 'BROADCAST',
  BuildRPAJob = 'BUILD_RPA_JOB',
  CancelPayments = 'CANCEL_PAYMENTS',
  CheckInsuranceRealTimeEligibility = 'CHECK_INSURANCE_REAL_TIME_ELIGIBILITY',
  CreateDocumentTemplate = 'CREATE_DOCUMENT_TEMPLATE',
  CreateEhrSystem = 'CREATE_EHR_SYSTEM',
  CreateExternalDocumentPackets = 'CREATE_EXTERNAL_DOCUMENT_PACKETS',
  CreateFrontDeskDevice = 'CREATE_FRONT_DESK_DEVICE',
  CreateNoteTemplates = 'CREATE_NOTE_TEMPLATES',
  CreateOrganization = 'CREATE_ORGANIZATION',
  CreatePayments = 'CREATE_PAYMENTS',
  CreatePayout = 'CREATE_PAYOUT',
  CreatePopulations = 'CREATE_POPULATIONS',
  CreatePractice = 'CREATE_PRACTICE',
  CreatePracticeGroup = 'CREATE_PRACTICE_GROUP',
  CreatePrevisitFormTemplates = 'CREATE_PREVISIT_FORM_TEMPLATES',
  CreatePrevisitLink = 'CREATE_PREVISIT_LINK',
  CreateServiceAccount = 'CREATE_SERVICE_ACCOUNT',
  CreateTestPatient = 'CREATE_TEST_PATIENT',
  CreateUser = 'CREATE_USER',
  CreateUserOrganization = 'CREATE_USER_ORGANIZATION',
  CreateUserPractice = 'CREATE_USER_PRACTICE',
  CreateUserPracticeGroup = 'CREATE_USER_PRACTICE_GROUP',
  CreateVisitReason = 'CREATE_VISIT_REASON',
  CreateVpnConnectionRequests = 'CREATE_VPN_CONNECTION_REQUESTS',
  CreateWorkflowTests = 'CREATE_WORKFLOW_TESTS',
  DeleteCareProviders = 'DELETE_CARE_PROVIDERS',
  DeleteDocumentTemplate = 'DELETE_DOCUMENT_TEMPLATE',
  DeleteNoteTemplates = 'DELETE_NOTE_TEMPLATES',
  DeleteOrganization = 'DELETE_ORGANIZATION',
  DeletePatient = 'DELETE_PATIENT',
  DeletePopulations = 'DELETE_POPULATIONS',
  DeletePracticeGroup = 'DELETE_PRACTICE_GROUP',
  DeletePrevisitForms = 'DELETE_PREVISIT_FORMS',
  DeletePrevisitFormTemplates = 'DELETE_PREVISIT_FORM_TEMPLATES',
  DeletePrevisitLink = 'DELETE_PREVISIT_LINK',
  DeleteServiceAccount = 'DELETE_SERVICE_ACCOUNT',
  DeleteTestPatient = 'DELETE_TEST_PATIENT',
  DeleteUser = 'DELETE_USER',
  DeleteUserOrganization = 'DELETE_USER_ORGANIZATION',
  DeleteUserPractice = 'DELETE_USER_PRACTICE',
  DeleteUserPracticeGroup = 'DELETE_USER_PRACTICE_GROUP',
  DeleteVisitReason = 'DELETE_VISIT_REASON',
  MakeFhirGetRequest = 'MAKE_FHIR_GET_REQUEST',
  MakeFhirPatchRequest = 'MAKE_FHIR_PATCH_REQUEST',
  MakeFhirPostRequest = 'MAKE_FHIR_POST_REQUEST',
  MakeFhirPutRequest = 'MAKE_FHIR_PUT_REQUEST',
  ManageCaptchaSettings = 'MANAGE_CAPTCHA_SETTINGS',
  ManageExportNoteWorkflow = 'MANAGE_EXPORT_NOTE_WORKFLOW',
  ManageExportPatientUploadsWorkflow = 'MANAGE_EXPORT_PATIENT_UPLOADS_WORKFLOW',
  ManageExportProblemGroupsWorkflow = 'MANAGE_EXPORT_PROBLEM_GROUPS_WORKFLOW',
  ManagePrevisitWorkflow = 'MANAGE_PREVISIT_WORKFLOW',
  ManageStripeAccount = 'MANAGE_STRIPE_ACCOUNT',
  ModifyAgentConversation = 'MODIFY_AGENT_CONVERSATION',
  ModifyAgentConversationResponseSchedule = 'MODIFY_AGENT_CONVERSATION_RESPONSE_SCHEDULE',
  ModifyAgentConversationResponseTemplate = 'MODIFY_AGENT_CONVERSATION_RESPONSE_TEMPLATE',
  ModifyAssistantConfiguration = 'MODIFY_ASSISTANT_CONFIGURATION', // Web Assistant Configuration
  ModifyAssistantPhoneConfiguration = 'MODIFY_ASSISTANT_PHONE_CONFIGURATION', // Voice Assistant Phone Configuration
  ModifyAssistantPhoneRouting = 'MODIFY_ASSISTANT_PHONE_ROUTING', // Voice Assistant Phone Practice Routing
  ModifyAssistantPrompts = 'MODIFY_ASSISTANT_PROMPTS', // Web Assistant Prompts
  ModifyVoiceAssistant = 'MODIFY_VOICE_ASSISTANT', // Voice Assistant Configuration
  ModifyCareProviders = 'MODIFY_CARE_PROVIDERS',
  ModifyClaimLineDenialReports = 'MODIFY_CLAIM_LINE_DENIAL_REPORTS',
  ModifyDashboardEmbeds = 'MODIFY_DASHBOARD_EMBEDS',
  ModifyDecisionTrees = 'MODIFY_DECISION_TREES',
  ModifyEmailSubscription = 'MODIFY_EMAIL_SUBSCRIPTION',
  ModifyExternalDocumentPackets = 'MODIFY_EXTERNAL_DOCUMENT_PACKETS',
  ModifyExternalUserGroups = 'MODIFY_EXTERNAL_USER_GROUPS',
  ModifyFeeds = 'MODIFY_FEEDS',
  ModifyFrontDeskDevice = 'MODIFY_FRONT_DESK_DEVICE',
  ModifyGroups = 'MODIFY_GROUPS',
  ModifyImpersonation = 'MODIFY_IMPERSONATION',
  ModifyInterfaceEngineConnections = 'MODIFY_INTERFACE_ENGINE_CONNECTIONS',
  ModifyInternalUserGroups = 'MODIFY_INTERNAL_USER_GROUPS',
  ModifyNoteMacros = 'MODIFY_NOTE_MACROS',
  ModifyNotificationContents = 'MODIFY_NOTIFICATION_CONTENTS',
  ModifyNotificationSchedules = 'MODIFY_NOTIFICATION_SCHEDULES',
  ModifyNotificationSchemes = 'MODIFY_NOTIFICATION_SCHEMES',
  ModifyPayors = 'MODIFY_PAYORS',
  ModifyPharmacies = 'MODIFY_PHARMACIES',
  ModifyPhoneNumberStatus = 'MODIFY_PHONE_NUMBER_STATUS',
  ModifyPracticeThemes = 'MODIFY_PRACTICE_THEMES',
  ModifyPrevisitLinks = 'MODIFY_PREVISIT_LINKS',
  ModifyProviderNoteFields = 'MODIFY_PROVIDER_NOTE_FIELDS',
  ModifyProviderNoteSections = 'MODIFY_PROVIDER_NOTE_SECTIONS',
  ModifyRecommendationTemplates = 'MODIFY_RECOMMENDATION_TEMPLATES',
  ModifyRteInsuranceCoverages = 'MODIFY_RTE_INSURANCE_COVERAGES',
  ModifySamlSettings = 'MODIFY_SAML_SETTINGS',
  ModifyScenarios = 'MODIFY_SCENARIOS',
  ModifyStandardNoteFields = 'MODIFY_STANDARD_NOTE_FIELDS',
  ModifyStandardNoteSections = 'MODIFY_STANDARD_NOTE_SECTIONS',
  ModifyStandardProcedures = 'MODIFY_STANDARD_PROCEDURES',
  ModifyUserFeatureFlags = 'MODIFY_USER_FEATURE_FLAGS',
  ModifyUserPreferences = 'MODIFY_USER_PREFERENCES',
  ModifyVisitReasons = 'MODIFY_VISIT_REASONS',
  ModifyWebhooks = 'MODIFY_WEBHOOKS',
  ModifyWorkflows = 'MODIFY_WORKFLOWS',
  ModifyWorkflowTests = 'MODIFY_WORKFLOW_TESTS',
  PatientDataStoreGetRequest = 'PATIENT_DATA_STORE_GET_REQUEST',
  PreviewDocumentTemplate = 'PREVIEW_DOCUMENT_TEMPLATE',
  RecoverUser = 'RECOVER_USER',
  RefundPayments = 'REFUND_PAYMENTS',
  ResetFrontDeskDevice = 'RESET_FRONT_DESK_DEVICE',
  ResetServiceAccount = 'RESET_SERVICE_ACCOUNT',
  ResetUserPassword = 'RESET_USER_PASSWORD',
  RetryWorkflows = 'RETRY_WORKFLOWS',
  Search = 'SEARCH',
  SendOutreach = 'SEND_OUTREACH',
  StaffDrivenCheckIn = 'STAFF_DRIVEN_CHECK_IN',
  StaffDrivenPayments = 'STAFF_DRIVEN_PAYMENTS',
  SubmitAuthorizationViaApi = 'SUBMIT_AUTHORIZATION_VIA_API',
  TestScenario = 'TEST_SCENARIO',
  TestWorkflow = 'TEST_WORKFLOW',
  TriggerCalendarSync = 'TRIGGER_CALENDAR_SYNC',
  TriggerOutreachSync = 'TRIGGER_OUTREACH_SYNC',
  TriggerPatientOrderSync = 'TRIGGER_PATIENT_ORDER_SYNC',
  UpdateDocumentTemplate = 'UPDATE_DOCUMENT_TEMPLATE',
  UpdateEncounterSummary = 'UPDATE_ENCOUNTER_SUMMARY',
  UpdateNoteTemplates = 'UPDATE_NOTE_TEMPLATES',
  UpdateOrganization = 'UPDATE_ORGANIZATION',
  UpdatePatientEvidence = 'UPDATE_PATIENT_EVIDENCE',
  UpdatePatientFindings = 'UPDATE_PATIENT_FINDINGS',
  UpdatePatientPrevisitForms = 'UPDATE_PATIENT_PREVISIT_FORMS',
  UpdatePopulations = 'UPDATE_POPULATIONS',
  UpdatePractice = 'UPDATE_PRACTICE',
  UpdatePracticeGroup = 'UPDATE_PRACTICE_GROUP',
  UpdatePrevisitFormTemplates = 'UPDATE_PREVISIT_FORM_TEMPLATES',
  UpdatePrevisitLink = 'UPDATE_PREVISIT_LINK',
  UpdateProvider = 'UPDATE_PROVIDER',
  UpdateProviderOrganizationPreference = 'UPDATE_PROVIDER_ORGANIZATION_PREFERENCE',
  UpdateServiceAccount = 'UPDATE_SERVICE_ACCOUNT',
  UpdateTestPatient = 'UPDATE_TEST_PATIENT',
  UpdateUser = 'UPDATE_USER',
  UpdateUserRole = 'UPDATE_USER_ROLE',
  UpdateVisitReason = 'UPDATE_VISIT_REASON',
  UpdateWorkflowTests = 'UPDATE_WORKFLOW_TESTS',
  UploadFeedItems = 'UPLOAD_FEED_ITEMS',
  ViewAgentConversation = 'VIEW_AGENT_CONVERSATION',
  ViewAssignmentGroups = 'VIEW_ASSIGNMENT_GROUPS',
  ViewAndModifyAssignmentGroups = 'VIEW_AND_MODIFY_ASSIGNMENT_GROUPS',
  ViewAllDashboardEmbeds = 'VIEW_ALL_DASHBOARD_EMBEDS', // removes organization-restriction from viewing report
  AccessSelfServeAnalytics = 'ACCESS_SELF_SERVE_ANALYTICS', // access to /reports/create (Omni self-serve analytics)
  ViewAssistantAudit = 'VIEW_ASSISTANT_AUDIT',
  ViewAssistantPhoneConfiguration = 'VIEW_ASSISTANT_PHONE_CONFIGURATION', // Voice Assistant Phone Configuration
  ViewVoiceAssistant = 'VIEW_VOICE_ASSISTANT', // Voice Assistant Configuration
  ViewClaimLineDenialReports = 'VIEW_CLAIM_LINE_DENIAL_REPORTS',
  ViewDashboardEmbedsIncludingPhi = 'VIEW_DASHBOARD_EMBEDS_INCLUDING_PHI',
  ViewDashboardEmbedsNoPHI = 'VIEW_DASHBOARD_EMBEDS_NO_PHI',
  ViewUtilizationDashboard = 'VIEW_UTILIZATION_DASHBOARD',
  ViewDecisionTrees = 'VIEW_DECISION_TREES',
  ViewEhrTwoFactor = 'VIEW_EHR_TWO_FACTOR',
  ViewEhrSystems = 'VIEW_EHR_SYSTEMS',
  ViewEligiblePatientsForOutreach = 'VIEW_ELIGIBLE_PATIENTS_FOR_OUTREACH',
  ViewEmailSubscription = 'VIEW_EMAIL_SUBSCRIPTION',
  ViewEncounters = 'VIEW_ENCOUNTERS',
  ViewEncounterSummary = 'VIEW_ENCOUNTER_SUMMARY',
  ViewExternalDocumentPackets = 'VIEW_EXTERNAL_DOCUMENT_PACKETS',
  ViewFeeds = 'VIEW_FEEDS',
  ViewFeedUploads = 'VIEW_FEED_UPLOADS',
  ViewInterfaceEngineDetails = 'VIEW_INTERFACE_ENGINE_DETAILS',
  ViewFrontDeskDevices = 'VIEW_FRONT_DESK_DEVICES',
  ViewInsuranceCards = 'VIEW_INSURANCE_CARDS',
  ViewNoteTemplates = 'VIEW_NOTE_TEMPLATES',
  ViewNotificationContents = 'VIEW_NOTIFICATION_CONTENTS',
  ViewNotificationSchedules = 'VIEW_NOTIFICATION_SCHEDULES',
  ViewNotificationSchemes = 'VIEW_NOTIFICATION_SCHEMES',
  ViewPatientEvidence = 'VIEW_PATIENT_EVIDENCE',
  ViewPatientFindings = 'VIEW_PATIENT_FINDINGS',
  ViewPatientSSNs = 'VIEW_PATIENT_SSNS',
  ViewPayments = 'VIEW_PAYMENTS',
  ViewPaymentsExpanded = 'VIEW_PAYMENTS_EXPANDED',
  ViewPaymentsTimeline = 'VIEW_PAYMENTS_TIMELINE',
  ViewPayors = 'VIEW_PAYORS',
  ViewPharmacies = 'VIEW_PHARMACIES',
  ViewPhoneNumberStatus = 'VIEW_PHONE_NUMBER_STATUS',
  ViewPopulations = 'VIEW_POPULATIONS',
  ViewBasicPractices = 'VIEW_BASIC_PRACTICES',
  ViewPractices = 'VIEW_PRACTICES',
  ViewPrevisitConversion = 'VIEW_PREVISIT_CONVERSION',
  ViewPrevisitFormTemplates = 'VIEW_PREVISIT_FORM_TEMPLATES',
  ViewPrevisitGeospatialConversion = 'VIEW_PREVISIT_GEOSPATIAL_CONVERSION',
  ViewPrevisitLinks = 'VIEW_PREVISIT_LINKS',
  ViewPrevisitUrl = 'VIEW_PREVISIT_URL',
  ViewProvider = 'VIEW_PROVIDER',
  ViewProviderOrganizationPreference = 'VIEW_PROVIDER_ORGANIZATION_PREFERENCE',
  ViewRecommendationTemplates = 'VIEW_RECOMMENDATION_TEMPLATES',
  ViewReconciliationFeesReport = 'VIEW_RECONCILIATION_FEES_REPORT',
  ViewScenarios = 'VIEW_SCENARIOS',
  ViewScheduledReports = 'VIEW_SCHEDULED_REPORTS',
  ViewServiceAccount = 'VIEW_SERVICE_ACCOUNT',
  ViewTestPatients = 'VIEW_TEST_PATIENTS',
  ViewTimeline = 'VIEW_TIMELINE',
  ViewTimelineBaseEvents = 'VIEW_TIMELINE_BASE_EVENTS',
  ViewTimelineFingerprintEvents = 'VIEW_TIMELINE_FINGERPRINT_EVENTS',
  ViewTimelineJobErrorEvents = 'VIEW_TIMELINE_JOB_ERROR_EVENTS',
  ViewTimelineJobEvents = 'VIEW_TIMELINE_JOB_EVENTS',
  ViewUserFeatureFlags = 'VIEW_USER_FEATURE_FLAGS',
  ViewUserOrganization = 'VIEW_USER_ORGANIZATION',
  ViewUserPracticeGroups = 'VIEW_USER_PRACTICE_GROUPS',
  ViewUserPractices = 'VIEW_USER_PRACTICES',
  ViewUserPreferences = 'VIEW_USER_PREFERENCES',
  ViewVisitReasons = 'VIEW_VISIT_REASONS',
  ViewVisitReasonsStandardValues = 'VIEW_VISIT_REASONS_STANDARD_VALUES',
  ViewVpnConnectionRequests = 'VIEW_VPN_CONNECTION_REQUESTS',
  ViewWebhooks = 'VIEW_WEBHOOKS',
  ViewWorkflows = 'VIEW_WORKFLOWS',
  ViewWorkflowTests = 'VIEW_WORKFLOW_TESTS',
}

export enum OrchestratorPermission {
  Copilot = 'COPILOT',
  CreateCanonicalFields = 'CREATE_CANONICAL_FIELDS',
  CreateOrchestratorJob = 'CREATE_ORCHESTRATOR_JOB',
  CheckOutCredentialS = 'CHECK_OUT_CREDENTIALS',
  CreateApiInstances = 'CREATE_API_INSTANCES',
  CreateEhrInstances = 'CREATE_EHR_INSTANCES',
  CreateDocumentTemplateConfigurations = 'CREATE_DOCUMENT_TEMPLATE_CONFIGURATIONS',
  CreatePracticeConfigs = 'CREATE_PRACTICE_CONFIGS',
  CreatePracticeConfigApiInstances = 'CREATE_PRACTICE_CONFIG_API_INSTANCES',
  CreatePracticeConfigEhrInstances = 'CREATE_PRACTICE_CONFIG_EHR_INSTANCES',
  CreateRotateCredential = 'CREATE_ROTATE_CREDENTIAL',
  DeleteApiInstances = 'DELETE_API_INSTANCES',
  DeleteEhrInstances = 'DELETE_EHR_INSTANCES',
  DeleteDocumentTemplateConfigurations = 'DELETE_DOCUMENT_TEMPLATE_CONFIGURATIONS',
  DeletePracticeConfigs = 'DELETE_PRACTICE_CONFIGS',
  DeletePracticeConfigApiInstances = 'DELETE_PRACTICE_CONFIG_API_INSTANCES',
  DeletePracticeConfigEhrInstances = 'DELETE_PRACTICE_CONFIG_EHR_INSTANCES',
  ManageManualJobs = 'MANAGE_MANUAL_JOBS',
  ModifyApiInstanceSecrets = 'MODIFY_API_INSTANCE_SECRETS',
  ModifyCredentialGroups = 'MODIFY_CREDENTIAL_GROUPS',
  ModifyEhrCredentials = 'MODIFY_EHR_CREDENTIALS',
  ModifyEhrDeploymentValidations = 'MODIFY_EHR_DEPLOYMENT_VALIDATIONS',
  ModifyJobConfigs = 'MODIFY_JOB_CONFIGS',
  ModifyOrchestratorConfigs = 'MODIFY_ORCHESTRATOR_CONFIGS',
  ModifySettingsAndSettingGroups = 'MODIFY_SETTINGS_AND_SETTING_GROUPS',
  ModifyPagerdutyServiceSecrets = 'MODIFY_PAGERDUTY_SERVICE_SECRETS',
  TakeManualJobs = 'TAKE_MANUAL_JOBS',
  UpdateApiInstances = 'UPDATE_API_INSTANCES',
  UpdateEhrInstances = 'UPDATE_EHR_INSTANCES',
  UpdateDocumentTemplateConfigurations = 'UPDATE_DOCUMENT_TEMPLATE_CONFIGURATIONS',
  UpdatePracticeConfigs = 'UPDATE_PRACTICE_CONFIGS',
  UpdatePracticeConfigApiInstances = 'UPDATE_PRACTICE_CONFIG_API_INSTANCES',
  UpdatePracticeConfigEhrInstances = 'UPDATE_PRACTICE_CONFIG_EHR_INSTANCES',
  ViewApiInstanceFields = 'VIEW_API_INSTANCE_FIELDS',
  ViewApiInstanceSecrets = 'VIEW_API_INSTANCE_SECRETS',
  ViewAppointmentKinds = 'VIEW_APPOINTMENT_KINDS',
  ViewBasicApiInstances = 'VIEW_BASIC_API_INSTANCES',
  ViewCanonicalFields = 'VIEW_CANONICAL_FIELDS',
  ViewCanonicalFieldTypes = 'VIEW_CANONICAL_FIELD_TYPES',
  ViewCanonicalValues = 'VIEW_CANONICAL_VALUES',
  ViewCredentialGroups = 'VIEW_CREDENTIAL_GROUPS',
  ViewEhrCredentialPasswords = 'VIEW_EHR_CREDENTIAL_PASSWORDS',
  ViewEhrCredentials = 'VIEW_EHR_CREDENTIALS',
  ViewDocumentTemplateConfigurations = 'VIEW_DOCUMENT_TEMPLATE_CONFIGURATIONS',
  ViewEhrDeploymentValidations = 'VIEW_EHR_DEPLOYMENT_VALIDATIONS',
  ViewEhrInstances = 'VIEW_EHR_INSTANCES',
  ViewEhrInstanceFields = 'VIEW_EHR_INSTANCE_FIELDS',
  ViewApiInstances = 'VIEW_API_INSTANCES',
  ViewJobConfigs = 'VIEW_JOB_CONFIGS',
  ViewOrchestratorConfigs = 'VIEW_ORCHESTRATOR_CONFIGS',
  ViewOrchestratorJob = 'VIEW_ORCHESTRATOR_JOB',
  ViewSettingsAndSettingGroups = 'VIEW_SETTINGS_AND_SETTING_GROUPS',
  ViewPagerdutyServiceSecrets = 'VIEW_PAGERDUTY_SERVICE_SECRETS',
  ViewPracticeConfigs = 'VIEW_PRACTICE_CONFIGS',
  ViewPracticeConfigApiInstances = 'VIEW_PRACTICE_CONFIG_API_INSTANCES',
  ViewPracticeConfigEhrInstances = 'VIEW_PRACTICE_CONFIG_EHR_INSTANCES',
  ViewUsers = 'VIEW_USERS',
}

export enum PredictPermission {
  ClaimLabelingJobs = 'CLAIM_LABELING_JOBS',
  EvaluateClinicalAlgorithms = 'EVALUATE_CLINICAL_ALGORITHMS',
  ManageLabelingJobs = 'MANAGE_LABELING_JOBS',
  PredictOrganizationDocumentsWrite = 'PREDICT_ORGANIZATION_DOCUMENTS_WRITE',
  ViewTextConversations = 'VIEW_TEXT_CONVERSATIONS',
  ViewVoiceConversations = 'VIEW_VOICE_CONVERSATIONS',
}

export enum CrawlerPermission {
  ViewCrawlerResources = 'VIEW_CRAWLER_RESOURCES',
  ModifyCrawlerResources = 'MODIFY_CRAWLER_RESOURCES',
}

export enum FlowPermission {
  ClaimHumanTasks = 'CLAIM_HUMAN_TASKS',
  ClaimHumanTasksInCategories = 'CLAIM_HUMAN_TASKS_IN_CATEGORIES',
  ExpireHumanTasks = 'EXPIRE_HUMAN_TASKS',
  UnclaimHumanTasksForOthers = 'UNCLAIM_HUMAN_TASKS_FOR_OTHERS',
  ModifyAgents = 'MODIFY_AGENTS',
  ModifyAgentsPublish = 'MODIFY_AGENTS_PUBLISH',
  ModifyFlowBuilderAncillary = 'MODIFY_FLOW_BUILDER_ANCILLARY',
  FlowBuilderDeveloperBlocks = 'FLOW_BUILDER_DEVELOPER_BLOCKS',
  ModifyFlowBuilderBlockMetadata = 'MODIFY_FLOW_BUILDER_BLOCK_METADATA',
  ViewAgents = 'VIEW_AGENTS',
  ViewFlowBuilderAncillary = 'VIEW_FLOW_BUILDER_ANCILLARY',
  ViewAllAgents = 'VIEW_ALL_AGENTS',
  ViewBasicHumanTaskCategories = 'VIEW_BASIC_HUMAN_TASK_CATEGORIES',
  ViewAndModifyHumanTaskCategories = 'VIEW_AND_MODIFY_HUMAN_TASK_CATEGORIES',
  ViewBlockDebugInfo = 'VIEW_BLOCK_DEBUG_INFO',
  ViewBulkFlowRunData = 'VIEW_BULK_FLOW_RUN_DATA',
  ExportBulkFlowRunData = 'EXPORT_BULK_FLOW_RUN_DATA',
  ViewMessageEventFlowTriggers = 'VIEW_MESSAGE_EVENT_FLOW_TRIGGERS',
  ModifyMessageEventFlowTriggers = 'MODIFY_MESSAGE_EVENT_FLOW_TRIGGERS',
  ViewKnowledgeBaseFiles = 'VIEW_KNOWLEDGE_BASE_FILES',
  UpdateKnowledgeBaseFiles = 'UPDATE_KNOWLEDGE_BASE_FILES',
  DeleteKnowledgeBaseFiles = 'DELETE_KNOWLEDGE_BASE_FILES',
  ModifyFlowStandardEvents = 'MODIFY_FLOW_STANDARD_EVENTS',
  ModifyFlowBuilderSkills = 'MODIFY_FLOW_BUILDER_SKILLS',
  PublishFlowBuilderSkills = 'PUBLISH_FLOW_BUILDER_SKILLS',
  ManageGlobalFlowTags = 'MANAGE_GLOBAL_FLOW_TAGS',
  ModifyFlowDefinitionTemplates = 'MODIFY_FLOW_DEFINITION_TEMPLATES',
  PublishFlowDefinitionTemplates = 'PUBLISH_FLOW_DEFINITION_TEMPLATES',
  ViewAdminDashboard = 'VIEW_ADMIN_DASHBOARD',
}

export enum AgenticPlatformPermission {
  UseChat = 'USE_AGENTIC_CHAT',
  UseMcp = 'USE_MCP',
}

export enum ResourceGatewayPermission {
  ViewConnectors = 'VIEW_CONNECTORS',
  ModifyConnectors = 'MODIFY_CONNECTORS',
  ViewConnections = 'VIEW_CONNECTIONS',
  ModifyConnections = 'MODIFY_CONNECTIONS',
  ViewIngestionJobs = 'VIEW_INGESTION_JOBS',
  ManageIngestionJobs = 'MANAGE_INGESTION_JOBS',
}

export const Permission = {
  ..._Permission,
  ...AgenticPlatformPermission,
  ...CrawlerPermission,
  ...FlowPermission,
  ...OrchestratorPermission,
  ...PredictPermission,
  ...ResourceGatewayPermission,
};
export type Permission =
  | _Permission
  | AgenticPlatformPermission
  | CrawlerPermission
  | FlowPermission
  | OrchestratorPermission
  | PredictPermission
  | ResourceGatewayPermission;

export interface BaseGroupPermission {
  groupId: string;
  name: Permission;
}
export interface GroupPermission extends BaseItem, BaseGroupPermission {}

export interface BaseGroup {
  name: string;
  description: string;
}
export interface UpdateValues extends Partial<BaseGroup> {}

export interface Group extends BaseGroup, BaseItem {}
export interface GroupWithPermissions extends Group {
  permissions: GroupPermission[];
}

export type FindAllOptions = Partial<{
  ids: string[];
  excludedIds: string[];
  q: string;
}>;

export interface WithPermissionFindAllOptions {
  permission?: Permission;
}
